/**
 * REPORTS SLICE
 *
 * Redux state for the reporting module. Covers three report levels (project,
 * release, scenario) and multiple sub-tabs per level (overview, trends,
 * defects, people, traceability, top-failing, runs, history,
 * releases-comparison), plus a shared filter bar and a drill-down panel.
 *
 * Note: the old date-range filter (`dateFilter` with preset/startDate/endDate)
 * was removed. Replaced by structural filters (status / priority / assignee /
 * labels / release).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchProjectExecutionSummary,
  fetchReleaseExecutionSummary,
  fetchScenarioExecutionSummary,
  fetchAllReleasesSummary,
  fetchTrends,
  fetchDefectAnalytics,
  fetchTesterProductivity,
  fetchTraceability,
  fetchTopFailing,
  fetchScenarioHistory,
  fetchDrillDown
} from '../../api/reportsApi'

// ============================================================================
// HELPER: extract axios error
// ============================================================================
const errorPayload = (error) => ({
  message: error.response?.data?.message || error.message,
  status: error.response?.status
})

// ============================================================================
// OVERVIEW THUNKS
// ============================================================================

export const fetchProjectReportAsync = createAsyncThunk(
  'reports/fetchProjectReport',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      return await fetchProjectExecutionSummary(projectId)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchReleaseReportAsync = createAsyncThunk(
  'reports/fetchReleaseReport',
  async ({ releaseId }, { rejectWithValue }) => {
    try {
      return await fetchReleaseExecutionSummary(releaseId)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchScenarioReportAsync = createAsyncThunk(
  'reports/fetchScenarioReport',
  async ({ scenarioId, includeRunBreakdown = true }, { rejectWithValue }) => {
    try {
      return await fetchScenarioExecutionSummary(scenarioId, includeRunBreakdown)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchAllReleasesReportAsync = createAsyncThunk(
  'reports/fetchAllReleasesReport',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      return await fetchAllReleasesSummary(projectId)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

// ============================================================================
// NEW REPORT TYPE THUNKS
// ============================================================================

export const fetchTrendsAsync = createAsyncThunk(
  'reports/fetchTrends',
  async ({ level, id, filters }, { rejectWithValue }) => {
    try {
      return await fetchTrends(level, id, filters)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchDefectsAsync = createAsyncThunk(
  'reports/fetchDefects',
  async ({ level, id, filters }, { rejectWithValue }) => {
    try {
      return await fetchDefectAnalytics(level, id, filters)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchPeopleAsync = createAsyncThunk(
  'reports/fetchPeople',
  async ({ level, id, filters }, { rejectWithValue }) => {
    try {
      return await fetchTesterProductivity(level, id, filters)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchTraceabilityAsync = createAsyncThunk(
  'reports/fetchTraceability',
  async ({ projectId, filters }, { rejectWithValue }) => {
    try {
      return await fetchTraceability(projectId, filters)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchTopFailingAsync = createAsyncThunk(
  'reports/fetchTopFailing',
  async ({ releaseId, filters }, { rejectWithValue }) => {
    try {
      return await fetchTopFailing(releaseId, filters)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchScenarioHistoryAsync = createAsyncThunk(
  'reports/fetchScenarioHistory',
  async ({ scenarioId }, { rejectWithValue }) => {
    try {
      return await fetchScenarioHistory(scenarioId)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

export const fetchDrillDownAsync = createAsyncThunk(
  'reports/fetchDrillDown',
  async (criteria, { rejectWithValue }) => {
    try {
      return await fetchDrillDown(criteria)
    } catch (error) {
      return rejectWithValue(errorPayload(error))
    }
  }
)

// ============================================================================
// INITIAL STATE
// ============================================================================

const emptyFilters = {
  statuses: [],
  priorities: [],
  assigneeIds: [],
  labelIds: [],
  releaseIds: []
}

const initialState = {
  // Which level the user is currently viewing.
  currentLevel: 'scenario', // 'project' | 'release' | 'scenario'

  // Which sub-tab within the level is active. Defaults to overview so existing
  // users see the familiar view when they open the modal.
  currentTab: 'overview', // 'overview'|'trends'|'defects'|'people'|'traceability'|'topFailing'|'runs'|'history'|'releasesComparison'

  // Context IDs for each level.
  context: {
    projectId: null,
    releaseId: null,
    scenarioId: null
  },

  // Shared filter criteria applied across tabs.
  filters: { ...emptyFilters },

  // Overview data (unchanged shape).
  projectReport: null,
  releaseReport: null,
  scenarioReport: null,
  releasesSummary: [],

  // Per-tab data, keyed by tab name.
  trends: null,
  defects: null,
  people: null,
  traceability: null,
  topFailing: null,
  history: null,

  // Drill-down panel state.
  drillDown: {
    open: false,
    criteria: null,
    items: [],
    loading: false,
    error: null
  },

  // Per-slice loading + error tracking. Using nested objects (not top-level
  // fields) keeps the shape flat when new tabs are added.
  loading: {
    project: false,
    release: false,
    scenario: false,
    releasesSummary: false,
    trends: false,
    defects: false,
    people: false,
    traceability: false,
    topFailing: false,
    history: false
  },
  errors: {
    project: null,
    release: null,
    scenario: null,
    releasesSummary: null,
    trends: null,
    defects: null,
    people: null,
    traceability: null,
    topFailing: null,
    history: null
  }
}

// ============================================================================
// SLICE
// ============================================================================

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setReportLevel: (state, action) => {
      state.currentLevel = action.payload
      // When switching level, reset back to overview so tabs valid for one
      // level aren't accidentally selected at another.
      state.currentTab = 'overview'
    },

    setReportTab: (state, action) => {
      state.currentTab = action.payload
    },

    setReportContext: (state, action) => {
      const { projectId, releaseId, scenarioId } = action.payload
      if (projectId !== undefined) state.context.projectId = projectId
      if (releaseId !== undefined) state.context.releaseId = releaseId
      if (scenarioId !== undefined) state.context.scenarioId = scenarioId
    },

    /**
     * Replace the entire filter object. Callers pass a full filter shape;
     * partial updates should compose on the selector side.
     */
    setFilters: (state, action) => {
      state.filters = { ...emptyFilters, ...action.payload }
    },

    clearFilters: (state) => {
      state.filters = { ...emptyFilters }
    },

    openDrillDown: (state, action) => {
      state.drillDown.open = true
      state.drillDown.criteria = action.payload
      state.drillDown.items = []
      state.drillDown.error = null
    },

    closeDrillDown: (state) => {
      state.drillDown.open = false
      state.drillDown.criteria = null
      state.drillDown.items = []
      state.drillDown.error = null
    },

    clearReports: (state) => {
      // Clear all fetched data + filters when the modal closes.
      state.projectReport = null
      state.releaseReport = null
      state.scenarioReport = null
      state.releasesSummary = []
      state.trends = null
      state.defects = null
      state.people = null
      state.traceability = null
      state.topFailing = null
      state.history = null
      state.filters = { ...emptyFilters }
      state.currentTab = 'overview'
      state.drillDown = {
        open: false,
        criteria: null,
        items: [],
        loading: false,
        error: null
      }
      state.errors = { ...initialState.errors }
    },

    clearError: (state, action) => {
      const key = action.payload
      if (state.errors[key] !== undefined) {
        state.errors[key] = null
      }
    }
  },
  extraReducers: (builder) => {
    // Helper factory: wire a thunk into loading/error/data slots.
    const wire = (thunk, key, applyData) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading[key] = true
          state.errors[key] = null
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading[key] = false
          applyData(state, action.payload)
          state.errors[key] = null
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading[key] = false
          state.errors[key] = action.payload?.message || `Failed to load ${key}`
        })
    }

    wire(fetchProjectReportAsync, 'project', (s, d) => { s.projectReport = d })
    wire(fetchReleaseReportAsync, 'release', (s, d) => { s.releaseReport = d })
    wire(fetchScenarioReportAsync, 'scenario', (s, d) => { s.scenarioReport = d })
    wire(fetchAllReleasesReportAsync, 'releasesSummary', (s, d) => { s.releasesSummary = d })
    wire(fetchTrendsAsync, 'trends', (s, d) => { s.trends = d })
    wire(fetchDefectsAsync, 'defects', (s, d) => { s.defects = d })
    wire(fetchPeopleAsync, 'people', (s, d) => { s.people = d })
    wire(fetchTraceabilityAsync, 'traceability', (s, d) => { s.traceability = d })
    wire(fetchTopFailingAsync, 'topFailing', (s, d) => { s.topFailing = d })
    wire(fetchScenarioHistoryAsync, 'history', (s, d) => { s.history = d })

    // Drill-down is tracked separately because it drives its own panel.
    builder
      .addCase(fetchDrillDownAsync.pending, (state) => {
        state.drillDown.loading = true
        state.drillDown.error = null
      })
      .addCase(fetchDrillDownAsync.fulfilled, (state, action) => {
        state.drillDown.loading = false
        state.drillDown.items = action.payload || []
        state.drillDown.error = null
      })
      .addCase(fetchDrillDownAsync.rejected, (state, action) => {
        state.drillDown.loading = false
        state.drillDown.error = action.payload?.message || 'Failed to load drill-down'
      })
  }
})

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  setReportLevel,
  setReportTab,
  setReportContext,
  setFilters,
  clearFilters,
  openDrillDown,
  closeDrillDown,
  clearReports,
  clearError
} = reportsSlice.actions

export const selectCurrentReportLevel = (state) => state.reports.currentLevel
export const selectCurrentReportTab = (state) => state.reports.currentTab
export const selectReportContext = (state) => state.reports.context
export const selectReportFilters = (state) => state.reports.filters
export const selectProjectReport = (state) => state.reports.projectReport
export const selectReleaseReport = (state) => state.reports.releaseReport
export const selectScenarioReport = (state) => state.reports.scenarioReport
export const selectReleasesSummary = (state) => state.reports.releasesSummary
export const selectTrends = (state) => state.reports.trends
export const selectDefects = (state) => state.reports.defects
export const selectPeople = (state) => state.reports.people
export const selectTraceability = (state) => state.reports.traceability
export const selectTopFailing = (state) => state.reports.topFailing
export const selectHistory = (state) => state.reports.history
export const selectDrillDown = (state) => state.reports.drillDown
export const selectReportLoading = (state) => state.reports.loading
export const selectReportErrors = (state) => state.reports.errors

export default reportsSlice.reducer
